import React, { ChangeEvent, useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Grid,
  Typography,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  FileText,
  Calendar,
  Tag,
  Clock,
  Pencil,
  Info,
  BookOpen,
  Settings as SettingsIcon,
} from 'lucide-react';
import { toast } from '../../../../../ui/toast';
import { DatePicker } from '../../../../../ui/input/date_picker/date_picker';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import {
  updateAssessment,
  updateAssessmentStatus,
} from '../../../../../utils/api_service/assessment_api';
import { Permission } from '../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../utils/hooks/PermissionGate';
import usePermissions from '../../../../../utils/hooks/usePermissions';
import {
  Assessment,
  BasicInfoInputs,
} from '../../../../../utils/types/assessment_types';

type AssessmentSummaryProps = {
  assessment: Assessment;
};

export const AssessmentSummary: React.FC<AssessmentSummaryProps> = ({
  assessment,
}) => {
  const { hasPermissions } = usePermissions();
  const [assessmentFormInfo, setAssessmentFormInfo] = useState<BasicInfoInputs>({
    title: assessment.assessmentTitle,
    description: assessment.description,
    startDate: new Date(assessment.startDate * 1000)
      .toISOString()
      .split('T')[0],
    endDate: new Date(assessment.endDate * 1000).toISOString().split('T')[0],
    type: assessment.assessmentType,
    jobPosition: assessment.jobPosition || undefined,
  });
  const [active, setActive] = useState<boolean>(assessment.isActive);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setAssessmentFormInfo({
      ...assessmentFormInfo,
      [event.target.name]: event.target.value,
    });
  };

  const changeDate = (timeRange: any) => {
    setAssessmentFormInfo({
      ...assessmentFormInfo,
      startDate: timeRange.startDate,
      endDate: timeRange.endDate,
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await updateAssessment(
        assessmentFormInfo,
        assessment.id,
      );
      if (response[0].id) {
        const statusResponse = await updateAssessmentStatus(
          assessment.id,
          active,
        );
        if (statusResponse.ok) {
          toast.success('Assessment Details Updated Successfully!', {
            position: 'bottom-center',
          });
        }
      }
    } catch (error) {
      toast.error('Failed to update assessment. Please try again.', {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Format dates for display
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', p: { xs: 2, md: 3 } }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Left Column: Overview card */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3.5,
              border: '1px solid',
              borderColor: 'divider',
              p: 3,
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={3}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2.5,
                    background: 'linear-gradient(to bottom right, #10b981, #2563eb)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'common.white',
                    flexShrink: 0,
                  }}
                >
                  <FileText size={24} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 18, lineHeight: 1.2 }}>
                    {assessment.assessmentTitle}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip
                      label={active ? 'Active' : 'Inactive'}
                      color={active ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                    <Chip
                      label={`${assessment.assessmentType} Assessment`}
                      color="primary"
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize', fontWeight: 600, fontSize: 11 }}
                    />
                  </Stack>
                </Box>
              </Stack>

              {/* Quick Stats */}
              <Stack spacing={2}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: 'success.50',
                    border: '1px solid',
                    borderColor: 'success.100',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600, display: 'block' }}>
                        Start Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.dark' }}>
                        {formatDate(assessment.startDate)}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'success.100', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'success.dark' }}>
                      <Calendar size={20} />
                    </Box>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: 'primary.50',
                    border: '1px solid',
                    borderColor: 'primary.100',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 600, display: 'block' }}>
                        End Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                        {formatDate(assessment.endDate)}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.100', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.dark' }}>
                      <Calendar size={20} />
                    </Box>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: 'secondary.50',
                    border: '1px solid',
                    borderColor: 'secondary.100',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" sx={{ color: 'secondary.dark', fontWeight: 600, display: 'block' }}>
                        Assessment ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'secondary.dark' }}>
                        {assessment.id}
                      </Typography>
                    </Box>
                    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'secondary.100', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'secondary.dark' }}>
                      <Tag size={20} />
                    </Box>
                  </Stack>
                </Paper>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Clock size={16} color="#6b7280" />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Last updated: {new Date().toLocaleDateString()}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Right Column: Configuration Form */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3.5,
              border: '1px solid',
              borderColor: 'divider',
              p: 3,
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(to bottom right, #3b82f6, #8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'common.white',
                }}
              >
                <Pencil size={20} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Assessment Configuration
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              {/* Basic Information */}
              <Grid item xs={12} lg={6}>
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, borderRadius: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Info size={18} color="#2563eb" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Basic Information
                    </Typography>
                  </Stack>
                  <Stack spacing={2}>
                    <TextInput
                      label="Assessment Name"
                      name="title"
                      value={assessmentFormInfo.title}
                      type="text"
                      onChange={handleChange}
                      disabled={!hasPermissions([Permission.ASSESSMENTS_EDIT])}
                    />
                    {assessment.assessmentType === 'candidate' && (
                      <TextInput
                        label="Job Title"
                        name="jobPosition"
                        value={assessmentFormInfo.jobPosition || ''}
                        type="text"
                        onChange={handleChange}
                        disabled={!hasPermissions([Permission.ASSESSMENTS_EDIT])}
                      />
                    )}
                  </Stack>
                </Paper>
              </Grid>

              {/* Schedule */}
              <Grid item xs={12} lg={6}>
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, borderRadius: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', height: '100%' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Calendar size={18} color="#059669" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Schedule
                    </Typography>
                  </Stack>
                  <DatePicker
                    label="Assessment Time Range"
                    startDate={assessmentFormInfo.startDate}
                    endDate={assessmentFormInfo.endDate}
                    onChange={changeDate}
                    disabled={!hasPermissions([Permission.ASSESSMENTS_EDIT])}
                  />
                </Paper>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{ p: 2.5, borderRadius: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <BookOpen size={18} color="#7c3aed" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Description
                    </Typography>
                  </Stack>
                  <TextArea
                    label=""
                    name="description"
                    value={assessmentFormInfo.description}
                    onChange={handleChange}
                    disabled={!hasPermissions([Permission.ASSESSMENTS_EDIT])}
                  />
                </Paper>
              </Grid>
            </Grid>

            {/* Status Section */}
            <PermissionGate requiredPermissions={[Permission.ASSESSMENTS_EDIT]}>
              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  p: 2.5,
                  borderRadius: 2.5,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <SettingsIcon size={18} color="#ea580c" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Assessment Status
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Control whether this assessment is active and accepting responses
                    </Typography>
                  </Box>
                  <ToggleInput
                    toggled={active}
                    handleToggle={() => setActive(!active)}
                    name="is_active"
                    informationText="Switch to make active/inactive"
                  />
                </Stack>
              </Paper>

              {/* Action Buttons */}
              <Divider sx={{ my: 3 }} />
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button
                  onClick={() => window.location.reload()}
                  variant="outlined"
                  color="inherit"
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Reset Changes
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={isLoading}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, minWidth: 120 }}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Stack>
            </PermissionGate>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
